import React from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useAuthTenant } from 'store/auth';
import {
  getComplianceMetrics,
  getRiskHeatmap,
  getDomainVulnerability,
  getLiabilityWatch,
  getVendorMonitoring,
  getAssessmentPipeline,
} from 'services/vendor-dashboard-service';
import { getVendorStats } from 'services/vendor-service';
import { RiskHeatmap } from 'Components/dashboard/RiskHeatmap';
import { DomainVulnerabilityRadar } from 'Components/dashboard/DomainVulnerabilityRadar';
import { Spinner } from 'Components/common/Spinner';
import { PageHeader } from 'Components/ui';

// ─── Layout ───────────────────────────────────────────────────────────────────

const DashContainer = styled.div`
  padding: 24px 24px 40px;
  background: var(--vo-mist);
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

// ─── Cards ─────────────────────────────────────────────────────────────────────

const WhiteCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 22px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

const KpiCard = styled(WhiteCard)`
  border-left: 4px solid ${({ $color }) => $color};
  padding: 20px 24px;
`;

const KpiLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin: 0 0 8px 0;
`;

const KpiValue = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 32px;
  font-weight: 800;
  color: ${({ $color }) => $color};
  letter-spacing: -0.02em;
  line-height: 1;
  margin: 0 0 4px 0;
`;

const KpiSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0;
`;

const CenteredCard = styled(WhiteCard)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

// ─── Grids ─────────────────────────────────────────────────────────────────────

const StatGrid4 = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;

  @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px)  { grid-template-columns: 1fr; }
`;

const StatsRow3 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;

  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const ChartGrid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  align-items: stretch;

  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const BottomRow2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

// ─── Card text ─────────────────────────────────────────────────────────────────

const CardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0;
  text-align: center;
`;

const CardSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0;
  text-align: center;
`;

const SectionCardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 4px 0;
`;

const SectionCardSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0 0 16px 0;
`;

// ─── Donut ─────────────────────────────────────────────────────────────────────


const DonutWrap = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
`;

const DonutOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const DonutPct = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 26px;
  font-weight: 800;
  color: ${({ $color }) => $color};
  margin: 0;
  letter-spacing: -0.02em;
`;

const PairRow = styled.div`
  display: flex;
  gap: 16px;
`;

const PairDivider = styled.div`
  width: 1px;
  background: var(--vo-cloud);
`;

const PairBox = styled.div`
  text-align: center;
`;

const PairValue = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  margin: 0;
`;

const PairLabel = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  color: var(--vo-slate);
  margin: 0;
`;

// ─── Score card ────────────────────────────────────────────────────────────────

const ScoreValue = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 52px;
  font-weight: 800;
  color: ${({ $color }) => $color};
  margin: 0;
  letter-spacing: -0.03em;
  line-height: 1;
`;

const ScoreBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  padding: 4px 14px;
  border-radius: 20px;
`;

const ScoreHighLow = styled.div`
  display: flex;
  gap: 16px;
  padding-top: 4px;
  border-top: 1px solid var(--vo-mist);
  width: 100%;
`;

const ScoreHLItem = styled.div`
  flex: 1;
  text-align: center;
`;

const ScoreHLLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin: 0 0 3px 0;
`;

const ScoreHLValue = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  margin: 0;
`;

// ─── Critical table ────────────────────────────────────────────────────────────

const CriticalHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const AlertCountBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: var(--vo-danger);
  background: var(--vo-danger-10);
  padding: 4px 14px;
  border-radius: 20px;
  flex-shrink: 0;
`;

const MonitorTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const MonitorThead = styled.thead`
  background: ${({ $critical }) => $critical ? 'var(--vo-danger-10)' : 'var(--vo-mist)'};
`

const MonitorTh = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: .08em;
  padding: 11px 16px;
  text-align: left;
  border-bottom: 1px solid ${({ $critical }) => $critical ? 'var(--vo-danger-15)' : 'var(--vo-cloud)'};
`

const MonitorTr = styled.tr`
  border-bottom: 1px solid ${({ $critical }) => $critical ? 'var(--vo-danger-10)' : 'var(--vo-mist)'};
  &:last-child { border-bottom: none; }
  &:hover { background: ${({ $critical }) => $critical ? 'var(--vo-danger-10)' : 'var(--vo-capri-25)'}; }
`

const MonitorTd = styled.td`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: var(--vo-ink);
  padding: 12px 16px;
`;

const VendorName = styled.span`
  font-weight: 500;
`;

const TierBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
`;

const ScoreChip = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg || 'transparent'};
  padding: ${({ $bg }) => $bg ? '3px 10px' : '0'};
  border-radius: ${({ $bg }) => $bg ? '20px' : '0'};
`

const ScoreBig = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: ${({ $color }) => $color};
`

const TableWrapper = styled.div`
  border: 1px solid var(--vo-cloud);
  border-radius: 10px;
  overflow: hidden;
`

const DPAChip = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  padding: 3px 10px;
  border-radius: 20px;
`;

const AlertChip = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  padding: 3px 10px;
  border-radius: 20px;
`;

const RecertText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ $color }) => $color};
`;

// ─── Tier distribution ─────────────────────────────────────────────────────────

const TIER_C = 2 * Math.PI * 38; // ≈ 238.76

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink);
  margin-bottom: 6px;
`;

const LegendDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const LegendCount = styled.span`
  margin-left: auto;
  font-family: 'Rubik', sans-serif;
  font-weight: 600;
`;

const ScoreBarSection = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--vo-mist);
`;

const ScoreBarTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 12px 0;
`;


const BarRow = styled.div`
  margin-bottom: 10px;
`;

const BarLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  margin-bottom: 4px;
`;

const BarTrack = styled.div`
  height: 6px;
  background: var(--vo-cloud);
  border-radius: 4px;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: ${({ $color }) => $color};
  border-radius: 4px;
`;

// ─── Pipeline ──────────────────────────────────────────────────────────────────

const PipelineItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: ${({ $bg }) => $bg};
  border-radius: 10px;
`;

const PipelineLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PipelineDot = styled.div`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const PipelineLabel = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: var(--vo-ink-2);
  font-weight: 500;
`;

const PipelineCount = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${({ $color }) => $color};
`;

// ─── Alert items ───────────────────────────────────────────────────────────────

const AlertItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: ${({ $bg }) => $bg};
  border-radius: 10px;
  border-left: 3px solid ${({ $border }) => $border};
`;

const AlertItemTitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--vo-ink-2);
  margin: 0 0 2px 0;
`;

const AlertItemSub = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0;
`;

// ─── Loader / empty ────────────────────────────────────────────────────────────

const LoaderWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 0;
`;

const EmptyMsg = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0;
  padding: 12px 0;
`;

// ─── Helpers ───────────────────────────────────────────────────────────────────

function getTierStyle(tier) {
  if (tier === 1) return { text: 'var(--vo-danger)', bg: 'var(--vo-danger-10)' };
  if (tier === 2) return { text: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' };
  return { text: 'var(--vo-success)', bg: 'var(--vo-success-10)' };
}

function getScoreColor(score) {
  if (score === null || score === undefined) return 'var(--vo-steel)';
  if (score >= 80) return 'var(--vo-success)';
  if (score >= 70) return 'var(--vo-warning)';
  return 'var(--vo-danger)';
}

function formatRecertDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString();
}

const getScoreBg = (score) => {
  if (score == null) return 'color-mix(in srgb, var(--vo-steel) 10%, transparent)'
  if (score >= 90) return 'var(--vo-success-10)'
  if (score >= 70) return 'var(--vo-warning-10)'
  return 'var(--vo-danger-10)'
}

function getScoreBadge(score) {
  if (score >= 85) return { labelKey: 'excellent', color: 'var(--vo-success)', bg: 'var(--vo-success-10)' };
  if (score >= 70) return { labelKey: 'good',      color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)'  };
  if (score >= 50) return { labelKey: 'regular',   color: 'var(--vo-sev-high)', bg: 'var(--vo-sev-high-12)'  };
  return             { labelKey: 'critical',  color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)'  };
}

// ─── Donut SVG helper ──────────────────────────────────────────────────────────

function DonutChart({ pct, color, size = 120, r = 48, strokeWidth = 12, children }) {
  const c = 2 * Math.PI * r;
  const filled = (pct / 100) * c;
  return (
    <DonutWrap style={{ width: size, height: size }}>
      <svg viewBox="0 0 120 120" width={size} height={size}>
        <circle cx="60" cy="60" r={r} fill="none" stroke="var(--vo-cloud)" strokeWidth={strokeWidth} />
        <circle
          cx="60" cy="60" r={r}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={`${filled} ${c}`}
          strokeLinecap="round"
          transform="rotate(-90 60 60)"
        />
      </svg>
      <DonutOverlay>{children}</DonutOverlay>
    </DonutWrap>
  );
}

// ─── Warning SVG ───────────────────────────────────────────────────────────────

const WarnSvg = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const InfoSvg = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

// ─── Main component ────────────────────────────────────────────────────────────

export const DashboardPage = () => {
  const { t } = useTranslation(['dashboard']);
  const selectedTenant = useSelector(useAuthTenant);
  const tenant = selectedTenant?.slug;

  const metricsQ = useQuery(
    ['dashboard-metrics', tenant],
    () => getComplianceMetrics(tenant),
    { enabled: !!tenant, staleTime: 5 * 60 * 1000 }
  );

  const vendorStatsQ = useQuery(
    ['vendorStats-riskDash', tenant],
    async () => {
      if (!tenant) return null;
      const r = await getVendorStats(tenant);
      return r?.data || null;
    },
    { enabled: !!tenant, staleTime: 5 * 60 * 1000 }
  );

  const heatmapQ = useQuery(
    ['dashboard-heatmap', tenant],
    () => getRiskHeatmap(tenant),
    { enabled: !!tenant, staleTime: 5 * 60 * 1000 }
  );

  const radarQ = useQuery(
    ['dashboard-radar', tenant],
    () => getDomainVulnerability(tenant),
    { enabled: !!tenant, staleTime: 5 * 60 * 1000 }
  );

  const liabilityQ = useQuery(
    ['dashboard-liability', tenant],
    () => getLiabilityWatch(tenant),
    { enabled: !!tenant, staleTime: 5 * 60 * 1000 }
  );

  const monitoringQ = useQuery(
    ['dashboard-monitoring', tenant],
    () => getVendorMonitoring(tenant),
    { enabled: !!tenant, staleTime: 5 * 60 * 1000 }
  );

  const pipelineQ = useQuery(
    ['dashboard-pipeline', tenant],
    () => getAssessmentPipeline(tenant),
    { enabled: !!tenant, staleTime: 5 * 60 * 1000 }
  );

  // ── Derived data ────────────────────────────────────────────────────────────

  const metrics       = metricsQ.data?.data?.success    ? metricsQ.data.data.data         : null;
  const vendorStats   = vendorStatsQ.data               || null;
  const heatmapData   = heatmapQ.data?.data?.success    ? heatmapQ.data.data.data         : null;
  const liabilityVendors = liabilityQ.data?.data?.success
    ? (liabilityQ.data.data.data?.vendors || [])
    : [];
  const criticalVendors = liabilityVendors.filter(
    v => v.classification === 'Unacceptable' || (v.trustScore != null && v.trustScore < 70)
  );
  const monitoringVendors = monitoringQ.data?.data?.success
    ? (monitoringQ.data.data.data?.vendors || [])
    : [];
  const pipeline = pipelineQ.data?.data?.success ? pipelineQ.data.data.data : null;

  const transformedRadarData = React.useMemo(() => {
    const r = radarQ.data?.data?.success ? radarQ.data.data.data : null;
    if (!r?.domains) return null;
    return r.domains.map((domain, i) => ({
      domain,
      score: r.scores[i] || 0,
      percentage: r.scores[i] || 0,
    }));
  }, [radarQ.data]);

  // KPI
  const totalVendors  = metrics?.totalVendors                              ?? vendorStats?.total                 ?? 0;
  const tier1Count    = metrics?.vendorsByTier?.tier1                      ?? 0;
  const pendingRecert = vendorStats?.pendingRecertification                ?? 0;

  // Compliance donut
  const complianceIndex = Math.round(metrics?.complianceIndex ?? 0);
  const conforming      = Math.round((complianceIndex / 100) * totalVendors);
  const nonConforming   = Math.max(0, totalVendors - conforming);

  // Score — no extra rounding: metrics.avgScore is already rounded to 1 decimal
  // server-side (DashboardService.getComplianceMetrics), and must match the value
  // shown on the main Dashboard (Pages/Dashboard/index.jsx), which prints it as-is.
  const avgScore = metrics?.avgScore ?? vendorStats?.averageTrustScore ?? 0;
  const badge    = getScoreBadge(avgScore);
  const highScore = metrics?.maxScore ?? 0;
  const lowScore  = metrics?.minScore ?? 0;

  // DPA — same reasoning as avgScore: no extra rounding, must match main Dashboard's raw display
  const dpaRate    = metrics?.dpaComplianceRate ?? vendorStats?.dpaCompliance ?? 0;
  const dpaSigned  = metrics?.dpaSigned ?? Math.round((dpaRate / 100) * totalVendors);
  const dpaUnsigned = Math.max(0, totalVendors - dpaSigned);

  // Tier distribution donut segments
  const t1 = metrics?.vendorsByTier?.tier1 ?? 0;
  const t2 = metrics?.vendorsByTier?.tier2 ?? 0;
  const t3 = metrics?.vendorsByTier?.tier3 ?? 0;
  const tierTotal = t1 + t2 + t3;
  let tierCum = 0;
  const tierSegs = tierTotal > 0
    ? [
        { color: 'var(--vo-danger)', count: t1, pct: t1 / tierTotal },
        { color: 'var(--vo-warning)', count: t2, pct: t2 / tierTotal },
        { color: 'var(--vo-success)', count: t3, pct: t3 / tierTotal },
      ].map(s => {
        const len = s.pct * TIER_C;
        const offset = -tierCum;
        tierCum += len;
        return { ...s, len, offset };
      })
    : [];

  // Alerts from critical vendors (max 3)
  const alertItems = criticalVendors.slice(0, 3).map(v => {
    const name = v.vendorName || v.name;
    const isDanger = !v.dpaEstablished || (v.trustScore != null && v.trustScore < 70);
    return {
      bg:     isDanger ? 'var(--vo-danger-10)' : 'var(--vo-heatmap-risky)',
      border: isDanger ? 'var(--vo-danger)' : 'var(--vo-warning)',
      isDanger,
      title: v.recertificationDue
        ? `${name} — ${t('dashboard:riskDash.alerts.certOverdue')}`
        : `${name} — ${t('dashboard:riskDash.alerts.pendingApproval')}`,
      sub: `Tier ${v.tier} · Trust Score: ${v.trustScore != null ? `${v.trustScore}%` : '—'}`,
    };
  });

  const pipelineItems = [
    { key: 'draft',             color: 'var(--vo-slate)', bg: 'var(--vo-mist)', count: pipeline?.draft ?? 0 },
    { key: 'waitingResponse',   color: 'var(--vo-warning)', bg: 'var(--vo-heatmap-risky)', count: pipeline?.waitingResponse ?? 0 },
    { key: 'inReview',          color: 'var(--vo-celeste)', bg: 'var(--vo-capri-50)', count: pipeline?.inReview ?? 0 },
    { key: 'executiveApproval', color: 'var(--vo-warning)', bg: 'var(--vo-heatmap-risky)', count: pipeline?.executiveApproval ?? 0 },
  ];

  const kpiLoading = metricsQ.isLoading || vendorStatsQ.isLoading || pipelineQ.isLoading;

  return (
    <DashContainer>

      {/* ── Page Header ────────────────────────────────────────────────── */}
      <PageHeader
        title={t('dashboard:riskDash.title')}
        segments={[
          { label: t('common:breadcrumbs.home') },
          { label: t('dashboard:riskDash.kpi.vendors') },
        ]}
      />

      {/* ── KPI Cards ──────────────────────────────────────────────────── */}
      <Section>
        {kpiLoading ? (
          <LoaderWrap><Spinner $size={40} /></LoaderWrap>
        ) : (
          <StatGrid4>
            <KpiCard $color="var(--vo-celeste)">
              <KpiLabel>{t('dashboard:riskDash.kpi.vendors')}</KpiLabel>
              <KpiValue $color="var(--vo-celeste)">{totalVendors}</KpiValue>
              <KpiSubtitle>{t('dashboard:riskDash.kpi.vendorsSub')}</KpiSubtitle>
            </KpiCard>
            <KpiCard $color="var(--vo-danger)">
              <KpiLabel>{t('dashboard:riskDash.kpi.tier1Critical')}</KpiLabel>
              <KpiValue $color="var(--vo-danger)">{tier1Count}</KpiValue>
              <KpiSubtitle>{t('dashboard:riskDash.kpi.tier1CriticalSub')}</KpiSubtitle>
            </KpiCard>
            <KpiCard $color="var(--vo-warning)">
              <KpiLabel>{t('dashboard:riskDash.kpi.pendingRecert')}</KpiLabel>
              <KpiValue $color="var(--vo-warning)">{pendingRecert}</KpiValue>
              <KpiSubtitle>{t('dashboard:riskDash.kpi.pendingRecertSub')}</KpiSubtitle>
            </KpiCard>
            <KpiCard $color="var(--vo-success)">
              <KpiLabel>{t('dashboard:riskDash.kpi.activeAssessments')}</KpiLabel>
              <KpiValue $color="var(--vo-success)">{pipeline?.total ?? 0}</KpiValue>
              <KpiSubtitle>{t('dashboard:riskDash.kpi.activeAssessmentsSub')}</KpiSubtitle>
            </KpiCard>
          </StatGrid4>
        )}
      </Section>

      {/* ── Stats Row: Compliance | Score | DPA ────────────────────────── */}
      <Section>
        {kpiLoading ? (
          <LoaderWrap><Spinner $size={40} /></LoaderWrap>
        ) : (
          <StatsRow3>

            {/* Índice de Conformidade */}
            <CenteredCard>
              <CardTitle>{t('dashboard:riskDash.compliance.title')}</CardTitle>
              <CardSubtitle>{t('dashboard:riskDash.compliance.subtitle')}</CardSubtitle>
              <DonutChart pct={complianceIndex} color="var(--vo-success)">
                <DonutPct $color="var(--vo-success)">{complianceIndex}%</DonutPct>
              </DonutChart>
              <PairRow>
                <PairBox>
                  <PairValue $color="var(--vo-success)">{conforming}</PairValue>
                  <PairLabel>{t('dashboard:riskDash.compliance.conforming')}</PairLabel>
                </PairBox>
                <PairDivider />
                <PairBox>
                  <PairValue $color="var(--vo-danger)">{nonConforming}</PairValue>
                  <PairLabel>{t('dashboard:riskDash.compliance.nonConforming')}</PairLabel>
                </PairBox>
              </PairRow>
            </CenteredCard>

            {/* Score Médio Geral */}
            <CenteredCard>
              <CardTitle>{t('dashboard:riskDash.avgScore.title')}</CardTitle>
              <CardSubtitle>{t('dashboard:riskDash.avgScore.subtitle')}</CardSubtitle>
              <ScoreValue $color={badge.color}>{avgScore}%</ScoreValue>
              <ScoreBadge $color={badge.color} $bg={badge.bg}>
                {t(`dashboard:riskDash.avgScore.${badge.labelKey}`)}
              </ScoreBadge>
              <ScoreHighLow>
                <ScoreHLItem>
                  <ScoreHLLabel>{t('dashboard:riskDash.avgScore.highest')}</ScoreHLLabel>
                  <ScoreHLValue $color="var(--vo-success)">{highScore}%</ScoreHLValue>
                </ScoreHLItem>
                <PairDivider />
                <ScoreHLItem>
                  <ScoreHLLabel>{t('dashboard:riskDash.avgScore.lowest')}</ScoreHLLabel>
                  <ScoreHLValue $color="var(--vo-danger)">{lowScore}%</ScoreHLValue>
                </ScoreHLItem>
              </ScoreHighLow>
            </CenteredCard>

            {/* DPA Compliance */}
            <CenteredCard>
              <CardTitle>{t('dashboard:riskDash.dpa.title')}</CardTitle>
              <CardSubtitle>{t('dashboard:riskDash.dpa.subtitle')}</CardSubtitle>
              <DonutChart pct={dpaRate} color="var(--vo-celeste)">
                <DonutPct $color="var(--vo-celeste)">{dpaRate}%</DonutPct>
              </DonutChart>
              <PairRow>
                <PairBox>
                  <PairValue $color="var(--vo-success)">{dpaSigned}</PairValue>
                  <PairLabel>{t('dashboard:riskDash.dpa.withDpa')}</PairLabel>
                </PairBox>
                <PairDivider />
                <PairBox>
                  <PairValue $color="var(--vo-danger)">{dpaUnsigned}</PairValue>
                  <PairLabel>{t('dashboard:riskDash.dpa.withoutDpa')}</PairLabel>
                </PairBox>
              </PairRow>
            </CenteredCard>

          </StatsRow3>
        )}
      </Section>

      {/* ── Charts: Heatmap + Radar ─────────────────────────────────────── */}
      <Section>
        <ChartGrid2>
          <div style={{ height: '100%' }}>
            {heatmapQ.isLoading
              ? <LoaderWrap><Spinner $size={40} /></LoaderWrap>
              : <RiskHeatmap data={heatmapData} />
            }
          </div>
          <div style={{ height: '100%' }}>
            {radarQ.isLoading
              ? <LoaderWrap><Spinner $size={40} /></LoaderWrap>
              : <DomainVulnerabilityRadar data={transformedRadarData} />
            }
          </div>
        </ChartGrid2>
      </Section>

      {/* ── Critical Monitoring Table ───────────────────────────────────── */}
      <Section>
        {liabilityQ.isLoading ? (
          <LoaderWrap><Spinner $size={40} /></LoaderWrap>
        ) : (
          <WhiteCard>
            <CriticalHeaderRow>
              <div>
                <SectionCardTitle>{t('dashboard:riskDash.criticalMonitoring.title')}</SectionCardTitle>
                <SectionCardSubtitle style={{ marginBottom: 0 }}>
                  {t('dashboard:riskDash.criticalMonitoring.subtitle')}
                </SectionCardSubtitle>
              </div>
              {criticalVendors.length > 0 && (
                <AlertCountBadge>
                  {t('dashboard:riskDash.criticalMonitoring.alertCount', { count: criticalVendors.length })}
                </AlertCountBadge>
              )}
            </CriticalHeaderRow>

            {criticalVendors.length === 0 ? (
              <EmptyMsg>{t('dashboard:riskDash.criticalMonitoring.noAlerts')}</EmptyMsg>
            ) : (
              <TableWrapper>
                <MonitorTable>
                  <MonitorThead $critical>
                    <tr>
                      <MonitorTh $critical>{t('dashboard:riskDash.criticalMonitoring.table.vendor')}</MonitorTh>
                      <MonitorTh $critical>{t('dashboard:riskDash.criticalMonitoring.table.tier')}</MonitorTh>
                      <MonitorTh $critical>{t('dashboard:riskDash.criticalMonitoring.table.trustScore')}</MonitorTh>
                      <MonitorTh $critical>{t('dashboard:riskDash.criticalMonitoring.table.dpa')}</MonitorTh>
                      <MonitorTh $critical>{t('dashboard:riskDash.criticalMonitoring.table.recertification')}</MonitorTh>
                      <MonitorTh $critical>{t('dashboard:riskDash.criticalMonitoring.table.alert')}</MonitorTh>
                    </tr>
                  </MonitorThead>
                  <tbody>
                    {criticalVendors.map((v) => {
                      const name      = v.vendorName || v.name;
                      const tier      = getTierStyle(v.tier);
                      const scoreColor = getScoreColor(v.trustScore);
                      const hasDpa    = !!v.dpaEstablished;
                      const isOverdue = !!v.recertificationDue;
                      const alertDesc = !hasDpa && v.trustScore != null && v.trustScore < 70
                        ? t('dashboard:riskDash.criticalMonitoring.alerts.criticalNoDpa')
                        : isOverdue
                          ? t('dashboard:riskDash.criticalMonitoring.alerts.certOverdue')
                          : t('dashboard:riskDash.criticalMonitoring.alerts.executivePending');
                      const isDanger  = !hasDpa || (v.trustScore != null && v.trustScore < 70);
                      const alertColor = isDanger ? 'var(--vo-danger)' : 'var(--vo-warning)';
                      const alertBg    = isDanger ? 'color-mix(in srgb, var(--vo-danger) 8%, transparent)' : 'color-mix(in srgb, var(--vo-warning) 8%, transparent)';

                      return (
                        <MonitorTr key={v.vendorId} $critical>
                          <MonitorTd><VendorName>{name}</VendorName></MonitorTd>
                          <MonitorTd>
                            <TierBadge $color={tier.text} $bg={tier.bg}>{t('dashboard:riskDash.tierLabel', { tier: v.tier })}</TierBadge>
                          </MonitorTd>
                          <MonitorTd>
                            {v.trustScore != null
                              ? <ScoreBig $color={scoreColor}>{v.trustScore}%</ScoreBig>
                              : <span style={{ color: 'var(--vo-steel)', fontStyle: 'italic' }}>{t('dashboard:riskDash.monitoringTable.noAssessment')}</span>
                            }
                          </MonitorTd>
                          <MonitorTd>
                            <DPAChip
                              $color={hasDpa ? 'var(--vo-success)' : 'var(--vo-danger)'}
                              $bg={hasDpa ? 'var(--vo-success-10)' : 'var(--vo-danger-10)'}
                            >
                              {hasDpa ? t('dashboard:riskDash.dpaYes') : t('dashboard:riskDash.dpaNo')}
                            </DPAChip>
                          </MonitorTd>
                          <MonitorTd>
                            <RecertText $color={isOverdue ? 'var(--vo-danger)' : 'var(--vo-warning)'}>
                              {formatRecertDate(v.recertificationDueDate) || (isOverdue ? t('dashboard:riskDash.criticalMonitoring.overdue') : '—')}
                            </RecertText>
                          </MonitorTd>
                          <MonitorTd>
                            <AlertChip $color={alertColor} $bg={alertBg}>{alertDesc}</AlertChip>
                          </MonitorTd>
                        </MonitorTr>
                      );
                    })}
                  </tbody>
                </MonitorTable>
              </TableWrapper>
            )}
          </WhiteCard>
        )}
      </Section>

      {/* ── Bottom Row: Distribution + Pipeline/Alerts ──────────────────── */}
      <Section>
        <BottomRow2>

          {/* Tier Distribution + Score bars */}
          <WhiteCard>
            <SectionCardTitle>{t('dashboard:riskDash.distribution.title')}</SectionCardTitle>
            <SectionCardSubtitle>{t('dashboard:riskDash.distribution.subtitle')}</SectionCardSubtitle>

            {kpiLoading ? <LoaderWrap><Spinner $size={32} /></LoaderWrap> : (
              <>
                <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                  {/* Donut */}
                  <svg viewBox="0 0 100 100" width="90" height="90" style={{ flexShrink: 0 }}>
                    <circle cx="50" cy="50" r="38" fill="none" stroke="var(--vo-cloud)" strokeWidth="16" />
                    {tierSegs.map((seg, i) => (
                      <circle
                        key={i}
                        cx="50" cy="50" r="38"
                        fill="none"
                        stroke={seg.color}
                        strokeWidth="16"
                        strokeDasharray={`${seg.len} ${TIER_C}`}
                        strokeDashoffset={seg.offset}
                        transform="rotate(-90 50 50)"
                      />
                    ))}
                    {tierTotal > 0 && (
                      <>
                        <text x="50" y="46" textAnchor="middle" fontSize="14" fontWeight="800" fontFamily="Rubik, sans-serif" fill="var(--vo-ink)">{tierTotal}</text>
                        <text x="50" y="57" textAnchor="middle" fontSize="7" fontFamily="Lato, sans-serif" fill="var(--vo-slate)">{t('dashboard:riskDash.totalLabel')}</text>
                      </>
                    )}
                  </svg>
                  {/* Legend */}
                  <div style={{ flex: 1 }}>
                    {[
                      { label: t('dashboard:riskDash.distribution.tier1'), color: 'var(--vo-danger)', count: t1 },
                      { label: t('dashboard:riskDash.distribution.tier2'), color: 'var(--vo-warning)', count: t2 },
                      { label: t('dashboard:riskDash.distribution.tier3'), color: 'var(--vo-success)', count: t3 },
                    ].map(item => (
                      <LegendRow key={item.label}>
                        <LegendDot $color={item.color} />
                        <span>{item.label}</span>
                        <LegendCount>{item.count}</LegendCount>
                      </LegendRow>
                    ))}
                  </div>
                </div>

                {/* Score per tier bars */}
                <ScoreBarSection>
                  <ScoreBarTitle>{t('dashboard:riskDash.distribution.scoreTitle')}</ScoreBarTitle>
                  {[
                    { label: t('dashboard:riskDash.distribution.tier1'), color: 'var(--vo-danger)', pct: metrics?.avgScoreByTier?.tier1 ?? 0 },
                    { label: t('dashboard:riskDash.distribution.tier2'), color: 'var(--vo-warning)', pct: metrics?.avgScoreByTier?.tier2 ?? 0 },
                    { label: t('dashboard:riskDash.distribution.tier3'), color: 'var(--vo-success)', pct: metrics?.avgScoreByTier?.tier3 ?? 0 },
                  ].map(item => (
                    <BarRow key={item.label}>
                      <BarLabelRow>
                        <span>{item.label}</span>
                        <span>{item.pct}%</span>
                      </BarLabelRow>
                      <BarTrack><BarFill $pct={item.pct} $color={item.color} /></BarTrack>
                    </BarRow>
                  ))}
                </ScoreBarSection>
              </>
            )}
          </WhiteCard>

          {/* Pipeline + Alertas */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

            <WhiteCard>
              <SectionCardTitle>{t('dashboard:riskDash.pipeline.title')}</SectionCardTitle>
              <SectionCardSubtitle>{t('dashboard:riskDash.pipeline.subtitle')}</SectionCardSubtitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {pipelineItems.map((item) => (
                  <PipelineItem key={item.key} $bg={item.bg}>
                    <PipelineLeft>
                      <PipelineDot $color={item.color} />
                      <PipelineLabel>{t(`dashboard:riskDash.pipeline.${item.key}`)}</PipelineLabel>
                    </PipelineLeft>
                    <PipelineCount $color={item.color}>{item.count}</PipelineCount>
                  </PipelineItem>
                ))}
              </div>
            </WhiteCard>

            <WhiteCard>
              <SectionCardTitle>{t('dashboard:riskDash.alertsSection.title')}</SectionCardTitle>
              <SectionCardSubtitle>{t('dashboard:riskDash.alertsSection.subtitle')}</SectionCardSubtitle>
              {alertItems.length === 0 ? (
                <EmptyMsg>{t('dashboard:riskDash.criticalMonitoring.noAlerts')}</EmptyMsg>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {alertItems.map((a, i) => (
                    <AlertItem key={i} $bg={a.bg} $border={a.border}>
                      {a.isDanger ? <WarnSvg color={a.border} /> : <InfoSvg color={a.border} />}
                      <div>
                        <AlertItemTitle>{a.title}</AlertItemTitle>
                        <AlertItemSub>{a.sub}</AlertItemSub>
                      </div>
                    </AlertItem>
                  ))}
                </div>
              )}
            </WhiteCard>

          </div>
        </BottomRow2>
      </Section>

      {/* ── Fornecedores em Monitoramento ───────────────────────────────── */}
      <Section>
        {monitoringQ.isLoading ? (
          <LoaderWrap><Spinner $size={40} /></LoaderWrap>
        ) : (
          <WhiteCard>
            <SectionCardTitle>{t('dashboard:riskDash.monitoringTable.title')}</SectionCardTitle>
            <SectionCardSubtitle>{t('dashboard:riskDash.monitoringTable.subtitle')}</SectionCardSubtitle>

            {monitoringVendors.length === 0 ? (
              <EmptyMsg>{t('dashboard:riskDash.monitoringTable.noData')}</EmptyMsg>
            ) : (
              <TableWrapper>
                <MonitorTable>
                  <MonitorThead>
                    <tr>
                      <MonitorTh>{t('dashboard:riskDash.monitoringTable.table.vendor')}</MonitorTh>
                      <MonitorTh>{t('dashboard:riskDash.monitoringTable.table.tier')}</MonitorTh>
                      <MonitorTh>{t('dashboard:riskDash.monitoringTable.table.trustScore')}</MonitorTh>
                      <MonitorTh>{t('dashboard:riskDash.monitoringTable.table.dpa')}</MonitorTh>
                      <MonitorTh>{t('dashboard:riskDash.monitoringTable.table.nextRecert')}</MonitorTh>
                    </tr>
                  </MonitorThead>
                  <tbody>
                    {monitoringVendors.map((v) => {
                      const name  = v.vendorName || v.name;
                      const tier  = getTierStyle(v.tier);
                      const hasDpa = !!v.dpaEstablished;
                      const isOverdue = !!v.recertificationDue;
                      const scoreColor = getScoreColor(v.trustScore);

                      return (
                        <MonitorTr key={v.vendorId}>
                          <MonitorTd><VendorName>{name}</VendorName></MonitorTd>
                          <MonitorTd>
                            <TierBadge $color={tier.text} $bg={tier.bg}>{t('dashboard:riskDash.tierLabel', { tier: v.tier })}</TierBadge>
                          </MonitorTd>
                          <MonitorTd>
                            {v.trustScore != null
                              ? <ScoreChip $color={scoreColor} $bg={getScoreBg(v.trustScore)}>{v.trustScore}%</ScoreChip>
                              : <span style={{ color: 'var(--vo-steel)', fontStyle: 'italic' }}>{t('dashboard:riskDash.monitoringTable.noAssessment')}</span>
                            }
                          </MonitorTd>
                          <MonitorTd>
                            <DPAChip
                              $color={hasDpa ? 'var(--vo-success)' : 'var(--vo-danger)'}
                              $bg={hasDpa ? 'var(--vo-success-10)' : 'var(--vo-danger-10)'}
                            >
                              {hasDpa ? t('dashboard:riskDash.dpaYes') : t('dashboard:riskDash.dpaNo')}
                            </DPAChip>
                          </MonitorTd>
                          <MonitorTd>
                            <RecertText $color={isOverdue ? 'var(--vo-danger)' : 'var(--vo-slate)'}>
                              {formatRecertDate(v.recertificationDueDate) || (isOverdue ? t('dashboard:riskDash.criticalMonitoring.overdue') : '—')}
                            </RecertText>
                          </MonitorTd>
                        </MonitorTr>
                      );
                    })}
                  </tbody>
                </MonitorTable>
              </TableWrapper>
            )}
          </WhiteCard>
        )}
      </Section>

    </DashContainer>
  );
};
